import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { fetchVersionChanges, fetchVersionInfo } from './versionApi';
import { VERSION_POLL_INTERVAL_MS, versionAckKey } from './versionNotice';

export const versionQueryKeys = {
  info: ['version', 'info'] as const,
  changes: (from: string | null, to: string | null) => ['version', 'changes', from, to] as const,
};

/**
 * The server's version, re-read every few minutes and when the tab becomes
 * visible again, so an open tab notices a deploy.
 */
export function useVersionInfo() {
  return useQuery({
    queryKey: versionQueryKeys.info,
    queryFn: fetchVersionInfo,
    refetchInterval: VERSION_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * `from: undefined` asks the server for its default baseline (production, in
 * a test environment). `enabled: false` skips the request.
 */
export function useVersionChanges(params: { from?: string; to?: string; enabled: boolean }) {
  return useQuery({
    queryKey: versionQueryKeys.changes(params.from ?? null, params.to ?? null),
    queryFn: () =>
      fetchVersionChanges({
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
      }),
    enabled: params.enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** localStorage key for the signed-in user's acknowledgement, or null. */
export function useVersionAckKey(): string | null {
  const user = useAuthStore((s) => s.user);
  return user ? versionAckKey(user.tenantId, user.id) : null;
}
